import { create } from 'zustand';
import { pushApi } from '@/api/endpoints';
import { navigateTo } from './navigation';
import { clientConfig } from './session';

/**
 * Installable-app and Web Push plumbing: registers the service worker, keeps
 * the browser's "install" prompt for a button, and manages this device's push
 * subscription.
 */

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface PwaState {
  installPrompt: BeforeInstallPromptEvent | null;
  installed: boolean;
}

export const usePwa = create<PwaState>(() => ({
  installPrompt: null,
  installed: typeof window !== 'undefined' && window.matchMedia?.('(display-mode: standalone)').matches,
}));

export const pushSupported = () => typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

export function registerServiceWorker() {
  if (typeof window === 'undefined') return;
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault(); // show our own button instead of the mini-infobar
    usePwa.setState({ installPrompt: e as BeforeInstallPromptEvent });
  });
  window.addEventListener('appinstalled', () => usePwa.setState({ installPrompt: null, installed: true }));
  if (!('serviceWorker' in navigator)) return;
  navigator.serviceWorker.addEventListener('message', (e: MessageEvent<{ type?: string; url?: string }>) => {
    if (e.data?.type === 'clanfire:navigate' && e.data.url?.startsWith('/')) navigateTo(e.data.url);
  });
  void navigator.serviceWorker.register('/sw.js').catch(() => undefined);
}

export async function promptInstall(): Promise<boolean> {
  const prompt = usePwa.getState().installPrompt;
  if (!prompt) return false;
  await prompt.prompt();
  const { outcome } = await prompt.userChoice;
  usePwa.setState({ installPrompt: null });
  return outcome === 'accepted';
}

function keyToBytes(base64url: string): Uint8Array<ArrayBuffer> {
  const bin = atob(base64url.replace(/-/g, '+').replace(/_/g, '/'));
  const bytes = new Uint8Array(new ArrayBuffer(bin.length));
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

/** This device's current push subscription, if any. */
export async function currentPushSubscription(): Promise<PushSubscription | null> {
  if (!pushSupported()) return null;
  const registration = await navigator.serviceWorker.getRegistration('/');
  return (await registration?.pushManager.getSubscription()) ?? null;
}

export type PushResult = 'enabled' | 'denied' | 'unsupported' | 'unavailable';

export async function enablePush(): Promise<PushResult> {
  if (!pushSupported()) return 'unsupported';
  const key = clientConfig.pushPublicKey;
  if (!key) return 'unavailable';
  const permission = Notification.permission === 'default' ? await Notification.requestPermission() : Notification.permission;
  if (permission !== 'granted') return 'denied';
  const registration = await navigator.serviceWorker.register('/sw.js');
  await navigator.serviceWorker.ready;
  let subscription = await registration.pushManager.getSubscription();
  // A subscription made with another server key can't receive our pushes.
  const existingKey = subscription?.options.applicationServerKey;
  if (subscription && existingKey && btoa(String.fromCharCode(...new Uint8Array(existingKey))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '') !== key) {
    await subscription.unsubscribe();
    subscription = null;
  }
  subscription ??= await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyToBytes(key) });
  const json = subscription.toJSON();
  await pushApi.subscribe({ endpoint: subscription.endpoint, keys: { p256dh: json.keys!.p256dh!, auth: json.keys!.auth! } });
  return 'enabled';
}

export async function disablePush(): Promise<void> {
  const subscription = await currentPushSubscription();
  if (!subscription) return;
  await pushApi.unsubscribe(subscription.endpoint).catch(() => undefined);
  await subscription.unsubscribe();
}
